import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Typography,
  Select,
  DatePicker,
  Space,
  Tag,
  Input,
  Button,
} from 'antd';
import { HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import { adminRoleService, AdminActionLog } from '../services/adminRoleService';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminActionLogs: React.FC = () => {
  const [logs, setLogs] = useState<AdminActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    resource: '',
    actionType: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadLogs();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await adminRoleService.getActionLogs({
        page: pagination.current,
        limit: pagination.pageSize,
        resource: filters.resource || undefined,
        actionType: filters.actionType || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setLogs(response.logs);
      setPagination(prev => ({ ...prev, total: response.pagination.totalItems }));
    } catch (error: any) {
      console.error('Failed to load action logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setFilters({
        ...filters,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      });
    } else {
      setFilters({
        ...filters,
        startDate: '',
        endDate: '',
      });
    }
  };

  const getActionTypeColor = (actionType: string): string => {
    const colors: Record<string, string> = {
      create: 'green',
      update: 'blue',
      delete: 'red',
      suspend: 'orange',
      ban: 'red',
      unblock: 'green',
      approve: 'green',
      reject: 'red',
      assign_role: 'purple',
      remove_role: 'orange',
      send_notification: 'cyan',
      modify_settings: 'gold',
    };
    return colors[actionType] || 'default';
  };

  const columns = [
    {
      title: 'Admin',
      key: 'admin',
      render: (record: AdminActionLog) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.admin.name || record.admin.username}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>@{record.admin.username}</div>
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      render: (actionType: string) => (
        <Tag color={getActionTypeColor(actionType)}>
          {actionType.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      render: (resource: string) => <Tag>{resource}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip: string) => ip || '-',
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: true,
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            <HistoryOutlined /> Admin Action Logs
          </Title>
        </div>

        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="Filter by Resource"
              style={{ width: 200 }}
              allowClear
              value={filters.resource || undefined}
              onChange={(value) => setFilters({ ...filters, resource: value || '' })}
            >
              <Option value="user">User</Option>
              <Option value="post">Post</Option>
              <Option value="report">Report</Option>
              <Option value="admin_role">Admin Role</Option>
              <Option value="badge">Badge</Option>
              <Option value="notification">Notification</Option>
            </Select>

            <Select
              placeholder="Filter by Action Type"
              style={{ width: 200 }}
              allowClear
              value={filters.actionType || undefined}
              onChange={(value) => setFilters({ ...filters, actionType: value || '' })}
            >
              <Option value="create">Create</Option>
              <Option value="update">Update</Option>
              <Option value="delete">Delete</Option>
              <Option value="suspend">Suspend</Option>
              <Option value="ban">Ban</Option>
              <Option value="unblock">Unblock</Option>
              <Option value="assign_role">Assign Role</Option>
              <Option value="remove_role">Remove Role</Option>
            </Select>

            <RangePicker
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
            />

            <Button
              icon={<SearchOutlined />}
              onClick={loadLogs}
            >
              Search
            </Button>
          </Space>
        </Space>

        <Table
          dataSource={logs}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} actions`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
        />
      </Card>
    </div>
  );
};

export default AdminActionLogs;

